/**
 * SSDocumentsTab — file attachments for a party.
 *
 * Implements the Documents tab from the technical design doc:
 *   - Upload new file (multipart) with type / title / expiry meta
 *   - List existing documents with download / delete
 *   - Soft-delete (toggles dateto)
 */
import React, { useEffect, useRef, useState } from 'react';
import { ssDocumentAPI } from '../../../services/ssApi';
import {
  COLOR, RADIUS,
  Button, Field, Input, Select, Textarea,
  Card, EmptyState, Spinner, ErrorBanner, SuccessBanner, Badge,
} from '../../../components/ss';

const fmtBytes = (n) => {
  if (n == null) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
};

const fmtDate = (s) => s ? new Date(s).toLocaleDateString() : '—';

export default function SSDocumentsTab({ partyId }) {
  const [docs, setDocs]       = useState([]);
  const [types, setTypes]     = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr]         = useState('');
  const [ok, setOk]           = useState('');
  const [meta, setMeta]       = useState({
    documenttypeid: '', title: '', description: '',
    issuanceauthority: '', expirationdate: '',
  });
  const fileRef = useRef(null);

  const load = async () => {
    setLoading(true); setErr('');
    try {
      const [d, t] = await Promise.all([
        ssDocumentAPI.listForParty(partyId),
        ssDocumentAPI.listTypes(),
      ]);
      setDocs(d?.data || []);
      setTypes(t?.data || []);
    } catch (e) { setErr(e.response?.data?.detail || 'Failed to load documents'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [partyId]);

  const handleUpload = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) { setErr('Pick a file first'); return; }
    setErr(''); setOk('');
    try {
      await ssDocumentAPI.upload(partyId, file, meta);
      setOk('Uploaded');
      setMeta({ documenttypeid: '', title: '', description: '', issuanceauthority: '', expirationdate: '' });
      if (fileRef.current) fileRef.current.value = '';
      load();
    } catch (e) {
      setErr(e.response?.data?.detail || 'Upload failed');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Soft-delete this document?')) return;
    try { await ssDocumentAPI.remove(id); setOk('Document removed'); load(); }
    catch (e) { setErr(e.response?.data?.detail || 'Delete failed'); }
  };

  const selectedType = types.find(t => String(t.documenttypeid) === String(meta.documenttypeid));

  return (
    <div>
      {err && <ErrorBanner onClose={() => setErr('')}>{err}</ErrorBanner>}
      {ok  && <SuccessBanner onClose={() => setOk('')}>{ok}</SuccessBanner>}

      {/* Upload form */}
      <Card padding={16} style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 12, color: COLOR.text }}>
          Upload new document
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Field label="Document type">
            <Select value={meta.documenttypeid} onChange={e => setMeta({ ...meta, documenttypeid: e.target.value })}>
              <option value="">— select —</option>
              {types.map(t => (
                <option key={t.documenttypeid} value={t.documenttypeid}>
                  {t.code} — {t.name}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Title">
            <Input value={meta.title} onChange={e => setMeta({ ...meta, title: e.target.value })} />
          </Field>
          <Field label="Issuing authority">
            <Input value={meta.issuanceauthority} onChange={e => setMeta({ ...meta, issuanceauthority: e.target.value })} />
          </Field>
          <Field label={`Expiration ${selectedType?.requiresexpiration ? '(required)' : ''}`}>
            <Input type="date" value={meta.expirationdate} onChange={e => setMeta({ ...meta, expirationdate: e.target.value })} />
          </Field>
          <Field label="Description" style={{ gridColumn: 'span 2' }}>
            <Textarea rows={2} value={meta.description} onChange={e => setMeta({ ...meta, description: e.target.value })} />
          </Field>
          <Field label="File" style={{ gridColumn: 'span 2' }}>
            <input ref={fileRef} type="file" style={{ fontSize: 13 }} />
          </Field>
        </div>
        <div style={{ marginTop: 12, display: 'flex', justifyContent: 'flex-end' }}>
          <Button onClick={handleUpload}>📎 Upload</Button>
        </div>
      </Card>

      {/* List */}
      {loading && <Spinner />}
      {!loading && !docs.length && (
        <EmptyState icon="📂" title="No documents" message="Attach an ID, contract, or other supporting file above." />
      )}
      {!loading && docs.length > 0 && (
        <Card padding={0}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead style={{ background: COLOR.grayBg, color: COLOR.textMuted, fontSize: 11, textTransform: 'uppercase' }}>
              <tr>
                <th style={th}>Title / File</th>
                <th style={th}>Type</th>
                <th style={th}>Size</th>
                <th style={th}>Issued</th>
                <th style={th}>Expires</th>
                <th style={th}>Status</th>
                <th style={{ ...th, textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {docs.map(d => {
                const expired = d.expirationdate && new Date(d.expirationdate) < new Date();
                const closed  = !!d.dateto;
                return (
                  <tr key={d.partydocumentid} style={{ borderTop: `1px solid ${COLOR.graySubtle}` }}>
                    <td style={td}>
                      <div style={{ fontWeight: 500, color: COLOR.text }}>{d.title || d.filename}</div>
                      <div style={{ fontSize: 11, color: COLOR.textMuted }}>{d.filename}</div>
                    </td>
                    <td style={td}>
                      {d.type_code ? <Badge color="blue">{d.type_code}</Badge> : <span style={{ color: COLOR.textMuted }}>—</span>}
                    </td>
                    <td style={td}>{fmtBytes(d.size_bytes)}</td>
                    <td style={td}>{fmtDate(d.issuancedate)}</td>
                    <td style={td}>
                      {fmtDate(d.expirationdate)}{' '}
                      {expired && <Badge color="red">Expired</Badge>}
                    </td>
                    <td style={td}>
                      {closed ? <Badge color="gray">Removed</Badge> : <Badge color="green">Active</Badge>}
                    </td>
                    <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <a
                        href={ssDocumentAPI.downloadUrl(d.partydocumentid)}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ marginRight: 8, fontSize: 12, color: COLOR.blue, textDecoration: 'none' }}
                      >⬇️ Download</a>
                      {!closed && (
                        <button
                          onClick={() => handleDelete(d.partydocumentid)}
                          style={{ border: 'none', background: 'transparent', color: COLOR.red, cursor: 'pointer', fontSize: 12 }}
                        >🗑️ Remove</button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}

const th = { padding: '8px 12px', textAlign: 'left', fontWeight: 600 };
const td = { padding: '10px 12px', verticalAlign: 'top' };
